import { Fragment } from 'react'
import { ChipInput, PickList, TextField } from '@bloo-team/ui'
import { notificationProblems, readNotificationSpec, writeNotificationSpec } from '@bloo-team/api-client'
import type { NotificationSpec } from '@bloo-team/api-client'
import { recipientProblem } from '../../../lib/workbook-blocks'
import { InlineKv, Section } from './parts'
import type { BlockEditorProps, BlockViewProps } from './types'

/**
 * Notification: mails the previous block's rows. It neither makes nor changes
 * data; its column choices are the previous block's fields.
 *
 * Legacy submit rules (notificationProblems / notificationForSave): the column
 * and row choices mean something only with "one mail for all rows"
 * (`sendMailForAll`) — then it needs all columns or ≥ 1 column, and all rows or
 * top rows 1..10,000. Off, they are saved empty.
 */

const RECIPIENTS = [['to', 'to'], ['cc', 'cc'], ['bcc', 'bcc']] as const

export function NotificationView({ block }: BlockViewProps) {
  const s = readNotificationSpec(block)
  const filled = RECIPIENTS.filter(([k]) => s[k].length > 0)
  return (
    <div className="wb-secs">
      <div className="wb-sec">
        <span className="wb-mail-subject">{s.subject || 'No subject'}</span>
      </div>
      <Section label="Recipients">
        {filled.length ? (
          <div className="wb-lrows">
            {filled.map(([k, label]) => (
              <Fragment key={k}>
                <span className="l">{label}</span>
                <div className="wb-chips">
                  {s[k].map((a) => <span key={a} className="bui-chip bui-chip-solid wb-chip-mono">{a}</span>)}
                </div>
              </Fragment>
            ))}
          </div>
        ) : (
          <p className="wb-empty">No recipients yet.</p>
        )}
      </Section>
      <Section label="Message">
        {s.body ? <p className="wb-mail-body">{s.body}</p> : <p className="wb-empty">—</p>}
      </Section>
      <Section label="Delivery">
        <InlineKv
          items={[
            ['mails', s.forAll ? 'one for all rows' : 'one per row'],
            ...(s.forAll
              ? ([
                  ['columns', s.allColumns ? 'all' : s.columns.join(', ')],
                  ['rows', s.allRows ? 'all' : s.topRows ? `top ${s.topRows}` : ''],
                ] as Array<[string, string]>)
              : []),
          ]}
        />
      </Section>
    </div>
  )
}

export function NotificationEditor({ block, index, onChange, prevFields }: BlockEditorProps) {
  const s = readNotificationSpec(block)
  const set = (patch: Partial<NotificationSpec>) => onChange(writeNotificationSpec(block, { ...s, ...patch }))
  // Keep chosen columns selectable even before the previous block has run.
  const columnOptions = [...new Set([...prevFields, ...s.columns])]
  const problems = notificationProblems(s)
  return (
    <div className="wb-secs">
      <Section label="Recipients">
        <div className="wb-lrows">
          {RECIPIENTS.map(([k, label]) => (
            <Fragment key={k}>
              <span className="l" aria-hidden="true">{label}</span>
              <ChipInput
                label={`Block ${index + 1} ${label}`}
                hideLabel
                values={s[k]}
                onChange={(next) => set({ [k]: next })}
                placeholder="add an address or $Field…"
                validate={recipientProblem}
              />
            </Fragment>
          ))}
        </div>
      </Section>
      <Section label="Message">
        <div className="wb-fields">
          <TextField
            className="wb-w-lg"
            label="subject"
            hint="$Field placeholders are filled from the rows"
            value={s.subject}
            onChange={(v) => set({ subject: v })}
          />
        </div>
        <label className="bui-field wb-cap">
          <span className="bui-label">body</span>
          <textarea
            className="hunt-editor"
            aria-label={`Block ${index + 1} mail body`}
            rows={4}
            value={s.body}
            onChange={(e) => set({ body: e.target.value })}
          />
        </label>
      </Section>
      <Section label="Delivery">
        <div className="wb-lrows" aria-label={`Block ${index + 1} consolidated mail`}>
          <span className="l">mails</span>
          <div className="wb-lrow-c">
            <label className="bui-check">
              <input type="checkbox" checked={s.forAll} onChange={(e) => set({ forAll: e.target.checked })} />
              <span>one mail for all rows</span>
            </label>
          </div>
          <span className="l">columns</span>
          <div className="wb-lrow-c">
            <label className="bui-check">
              <input type="checkbox" checked={s.allColumns} disabled={!s.forAll} onChange={(e) => set({ allColumns: e.target.checked })} />
              <span>all columns</span>
            </label>
            <PickList
              label="Columns"
              multi
              placeholder={prevFields.length ? 'columns…' : 'run the block above for its columns'}
              options={columnOptions}
              selected={s.columns}
              disabled={!s.forAll || s.allColumns || columnOptions.length === 0}
              onChange={(next) => set({ columns: next })}
            />
          </div>
          <span className="l">rows</span>
          <div className="wb-lrow-c">
            <label className="bui-check">
              <input type="checkbox" checked={s.allRows} disabled={!s.forAll} onChange={(e) => set({ allRows: e.target.checked })} />
              <span>all rows</span>
            </label>
            <TextField
              className="wb-w-xs"
              aria-label="top rows"
              placeholder="top N"
              value={s.topRows}
              isDisabled={!s.forAll || s.allRows}
              onChange={(v) => set({ topRows: v.replace(/[^\d]/g, '') })}
            />
            <span className="bui-hint">1 – 10,000</span>
          </div>
        </div>
        {!s.forAll ? (
          <span className="bui-hint">One mail per row. Columns and rows apply to a single mail for all rows.</span>
        ) : problems.length ? (
          <div className="bui-alert bui-alert-warning" role="alert">
            <span className="bui-alert-mark">!</span>
            <span>Notification incomplete: {problems.join('; ')}.</span>
          </div>
        ) : null}
      </Section>
    </div>
  )
}
